import { signMessage } from "@wagmi/core";
import { getWagmiConfig } from "@/lib/wallet";
import {
  createDrop,
  recordMint,
  createListing,
  cancelListing,
  buyListing,
  trackWalletConnect,
  recordFeeConfig,
  addDropItems,
  uploadDropAsset,
  saveGlobalFees,
  saveNetworkSettings,
} from "@/lib/wallet-actions.functions";

function buildMessage(action: string, wallet: string, payload: Record<string, unknown>) {
  const nonce = crypto.randomUUID();
  const issued_at = new Date().toISOString();
  const canonical = { action, wallet: wallet.toLowerCase(), nonce, issued_at, payload };
  return `RobotDex authorization\n\nAction: ${action}\nWallet: ${wallet}\nIssued: ${issued_at}\nNonce: ${nonce}\n\nPayload:\n${JSON.stringify(payload)}\n\n---\n${JSON.stringify(canonical)}`;
}

async function sign(action: string, address: string, payload: Record<string, unknown>) {
  const message = buildMessage(action, address, payload);
  const signature = await signMessage(getWagmiConfig(), { account: address as `0x${string}`, message });
  return { env: { address, signature, message } };
}

export type DropInput = {
  name: string;
  symbol: string;
  description?: string | null;
  logo_url?: string | null;
  chain_id: number;
  mint_price_wei: string;
  max_supply?: number | null;
  start_at?: string | null;
  end_at?: string | null;
};

export async function signedCreateDrop(address: string, drop: DropInput) {
  const payload = { name: drop.name, symbol: drop.symbol, chain_id: drop.chain_id, mint_price_wei: drop.mint_price_wei };
  const { env } = await sign("create_drop", address, payload);
  return createDrop({ data: { env, drop } });
}

export type DropItemInput = {
  name?: string | null;
  image_url: string;
  attributes?: { trait_type: string; value: string | number }[] | null;
};

export async function signedAddDropItems(address: string, drop_id: string, items: DropItemInput[]) {
  return addDropItems({ data: { address, drop_id, items } });
}


export async function signedRecordMint(address: string, args: { drop_id: string; qty: number; price_wei: string; tx_hash?: string | null; kind: "mint" | "mint_request" }) {
  const payload = { drop_id: args.drop_id, qty: args.qty, price_wei: args.price_wei };
  const { env } = await sign("mint", address, payload);
  return recordMint({ data: { env, ...args, tx_hash: args.tx_hash ?? null } });
}

export async function signedCreateListing(address: string, args: { drop_id: string; token_id?: string | null; price_wei: string }) {
  const payload = { drop_id: args.drop_id, price_wei: args.price_wei };
  const { env } = await sign("list", address, payload);
  return createListing({ data: { env, ...args, token_id: args.token_id ?? null } });
}

export async function signedCancelListing(address: string, listing_id: string) {
  const { env } = await sign("cancel_listing", address, { listing_id });
  return cancelListing({ data: { env, listing_id } });
}

export async function signedBuyListing(address: string, args: { listing_id: string; tx_hash?: string | null }) {
  const { env } = await sign("buy", address, { listing_id: args.listing_id });
  return buyListing({ data: { env, ...args, tx_hash: args.tx_hash ?? null } });
}

export async function reportWalletConnect(address: string) {
  return trackWalletConnect({ data: { address } });
}

export async function signedRecordFeeConfig(address: string, args: {
  chain_id: number | null;
  mint_bps: number;
  list_bps: number;
  creator_bps: number;
  recipient: string | null;
}) {
  const payload = { mint_bps: args.mint_bps, list_bps: args.list_bps, creator_bps: args.creator_bps, recipient: args.recipient };
  const { env } = await sign("fee_config", address, payload);
  return recordFeeConfig({ data: { env, ...args } });
}

export async function signedUploadDropAsset(
  address: string,
  args: { folder: "logos" | "gallery"; size: number; content_type: "image/webp"; data_b64: string },
) {
  return uploadDropAsset({ data: { address, ...args } });
}

export async function signedSaveGlobalFees(address: string, args: {
  mint_bps: number; list_bps: number; creator_bps: number; fee_recipient: string | null;
}) {
  const payload = { mint_bps: args.mint_bps, list_bps: args.list_bps, creator_bps: args.creator_bps };
  const { env } = await sign("save_global_fees", address, payload);
  return saveGlobalFees({ data: { env, ...args } });
}

export type NetworkSettingEntry = {
  chain_id: number;
  fee_recipient: string | null;
  create_fee_wei: string;
  enabled?: boolean;
};

export async function signedSaveNetworkSettings(address: string, entries: NetworkSettingEntry[]) {
  const payload = { chains: entries.map((e) => e.chain_id).sort() };
  const { env } = await sign("save_network_settings", address, payload);
  return saveNetworkSettings({ data: { env, entries } });
}
